<template>
  <MainCard
    class="w-full! mb-6 dark:bg-surface-primary! dark:border-dark-borde!"
  >
    <h4 class="font-semibold text-text-primary dark:text-text-primary-dark">
      Mis datos
    </h4>

    <div
      class="flex flex-row mt-4 justify-between"
      :class="{ 'flex-wrap': !isDesktop }"
    >
      <!-- Secuencia -->
      <div class="flex flex-col">
        <div
          v-if="companyName !== ''"
          class="text-text-primary dark:text-text-primary-dark font-semibold text-sm/6"
        >
          {{ companyName }}
        </div>

        <span
          class="text-text-primary dark:text-text-primary-dark font-semibold text-sm/6"
        >
          Secuencia de inversión:
        </span>

        <Dropdown
          v-if="!secuency"
          v-model="selectedSecuency"
          :options="options"
          optionLabel="description"
          optionValue="id"
          class="w-63.75"
          :placeholder="
            options.length === 0
              ? 'Sin secuencias disponibles'
              : dropdownPlaceholder
          "
          :disabled="options.length === 0"
          @change="handleSelectChange"
        />

        <span
          v-else
          class="text-text-primary dark:text-text-primary-dark font-normal text-sm/6"
        >
          Secuencia {{ secuency }}
        </span>
      </div>

      <!-- Botón mostrar/ocultar -->
      <div v-if="!isDesktop" class="flex justify-end">
        <CustomButton
          @click="showData = !showData"
          :label="showData ? 'Ocultar Saldo' : 'Ver Saldo'"
          :icon="showData ? 'fa-eye-slash' : 'fa-eye'"
        />
      </div>

      <!-- Tabla de saldos -->
      <table v-if="isDesktop || showData">
        <thead
          class="text-text-primary dark:text-text-primary-dark font-semibold text-sm/6"
        >
          <tr>
            <th class="text-left w-52">Saldo:</th>
            <th class="text-right w-52">Saldo CLP:</th>
            <th class="text-right w-52">Saldo USD:</th>
            <th class="text-right w-52">Saldo EUR:</th>
          </tr>
        </thead>
        <tbody v-if="balance">
          <tr v-for="(amount, index) in amountData" :key="index">
            <td
              class="w-56 text-left text-text-secondary dark:text-text-primary-dark"
            >
              {{ amount.label }}:
            </td>
            <td class="text-right" :class="getAmountColor(amount.amountCLP)">
              <template v-if="!isLoading">
                {{
                  amount.amountCLP === 0
                    ? "-"
                    : formatPricePersonalData(amount.amountCLP, "CLP")
                }}
              </template>
              <template v-else>
                <Skeleton class="w-full h-6 rounded-lg"></Skeleton>
              </template>
            </td>
            <td class="text-right" :class="getAmountColor(amount.amountUSD)">
              <template v-if="!isLoading">
                {{
                  amount.amountUSD === 0
                    ? "-"
                    : formatPricePersonalData(amount.amountUSD, "USD")
                }}
              </template>
              <template v-else>
                <Skeleton class="w-full h-6 rounded-lg"></Skeleton>
              </template>
            </td>
            <td class="text-right" :class="getAmountColor(amount.amountEUR)">
              <template v-if="!isLoading">
                {{
                  amount.amountEUR === 0
                    ? "-"
                    : formatPricePersonalData(amount.amountEUR, "EUR")
                }}
              </template>
              <template v-else>
                <Skeleton class="w-full h-6 rounded-lg"></Skeleton>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </MainCard>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, PropType, watch } from "vue";
import CustomButton from "./CustomButton.vue";
import { useAuthStore, useClientStore } from "../../stores";
import { useCurrencyFormatter } from "../../composables/useFormatPrice";
import { useWindowSize } from "../../composables/useWindowSize";

interface Balance {
  clienteRut: string;
  secuencia: number;
  saldoContable: {
    cLP: number;
    uSD: number;
    eUR: number;
  };
  saldoDisponible: {
    cLP: number;
    uSD: number;
    eUR: number;
  };
  saldoTransito: {
    cLP: number;
    uSD: number;
    eUR: number;
  };
  cLPTotal: number;
  uSDTotal: number;
  eURTotal: number;
  totalCLP: number;
}

/* ========================
   🧩 STORES & COMPOSABLES
======================== */
const authStore = useAuthStore();
const clientStore = useClientStore();
const { formatPricePersonalData } = useCurrencyFormatter();
const { isDesktop } = useWindowSize();

/* ========================
   ⚙️ PROPS & EMITS
======================== */
const props = defineProps({
  secuency: {
    type: String,
    default: null,
  },
  companyName: {
    type: String,
    default: "",
  },
  balance: {
    type: Array as PropType<Balance[]>,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
    required: false,
  },
});

const isLoading = computed(() => props.loading);

const emits = defineEmits<{
  (e: "secuency-change", value: number): void;
}>();

/* ========================
   🧠 STATE
======================== */
const selectedSecuency = ref<number | null>(null);
const showData = ref(false);

const amountData = computed(() => {
  if (!props.balance || props.balance.length === 0)
    return [
      {
        label: "Disponible",
        amountCLP: 0,
        amountUSD: 0,
        amountEUR: 0,
      },
      {
        label: "En Tránsito",
        amountCLP: 0,
        amountUSD: 0,
        amountEUR: 0,
      },
      {
        label: "Contable",
        amountCLP: 0,
        amountUSD: 0,
        amountEUR: 0,
      },
    ];

  // Buscar el balance correspondiente a la secuencia seleccionada
  const selectedBalance =
    props.balance.find((b) => b.secuencia === selectedSecuency.value) ||
    props.balance[0];

  return [
    {
      label: "Disponible",
      amountCLP: selectedBalance.saldoDisponible.cLP,
      amountUSD: selectedBalance.saldoDisponible.uSD,
      amountEUR: selectedBalance.saldoDisponible.eUR,
    },
    {
      label: "En Tránsito",
      amountCLP: selectedBalance.saldoTransito.cLP,
      amountUSD: selectedBalance.saldoTransito.uSD,
      amountEUR: selectedBalance.saldoTransito.eUR,
    },
    {
      label: "Contable",
      amountCLP: selectedBalance.saldoContable.cLP,
      amountUSD: selectedBalance.saldoContable.uSD,
      amountEUR: selectedBalance.saldoContable.eUR,
    },
  ];
});

/* ========================
   🔁 LIFECYCLE
======================== */
onMounted(() => {
  // Inicializar secuencia seleccionada
  const currentId = authStore.secuency;
  const secuencyList = clientStore.getClient()?.secuency ?? [];

  if (secuencyList.length > 0) {
    const match = secuencyList.find((s) => s.id === currentId);
    selectedSecuency.value = match ? match.id : secuencyList[0].id;
  }
});

// Watch para actualizar cuando cambian las opciones disponibles
watch(
  () => clientStore.getClient()?.secuency,
  (newSecuencyList) => {
    if (!newSecuencyList || newSecuencyList.length === 0) return;

    const currentId = authStore.secuency;
    const match = newSecuencyList.find((s) => s.id === currentId);

    if (match && selectedSecuency.value !== match.id) {
      selectedSecuency.value = match.id;
    } else if (!selectedSecuency.value) {
      selectedSecuency.value = newSecuencyList[0].id;
    }
  },
  { immediate: true }
);

/* ========================
   📊 COMPUTEDS
======================== */
const options = computed(() => {
  const secuencyList = clientStore.getClient()?.secuency ?? [];
  return secuencyList;
});

const dropdownPlaceholder = computed(() => {
  const current = options.value.find(
    (opt) => opt.id === selectedSecuency.value
  );
  return current ? current.description : "Selecciona una secuencia";
});

/* ========================
   ⚙️ METHODS
======================== */
function getAmountColor(amount: number) {
  return amount < 0 ? "text-icon-tertiary" : "text-icon-success";
}

function handleSelectChange(event: any) {
  const value = event.value as number;

  selectedSecuency.value = value;
  authStore.secuency = value;

  emits("secuency-change", value);
}
</script>
